import { Button, Group, Table } from '@mantine/core';
import { IconPencilPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { BranchSelector } from '../../components/BranchSelector';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { SearchField } from '../../components/SearchField';
import { Title } from '../../components/Title';
import { useFilter } from '../../hooks/useFilter';
import { useModal } from '../../hooks/useModal';
import { usePagination } from '../../hooks/usePagination';
import { whiteIconButtonStyle } from '../../theme/styling/buttonStyling';
import { defaultIconStyle } from '../../theme/styling/iconStyling';
import { queryWithFilterPageAndSearch } from '../../util/api/queryWithFilterPageAndSearch';
import { NewSupplierModal } from './components/NewSupplierModal';
import { SupplierRow } from './components/SupplierRow';
import type { SupplierDto } from './types/SupplierDto';
import { KeyedRow } from '../../components/KeyedRow';

export function Suppliers() {
	const { organisation } = useParams();
	const pagination = usePagination();
	const filter = useFilter();
	const [searchQuery, setSearchQuery] = useState('');
	const newModal = useModal();

	const suppliersCountQuery = queryWithFilterPageAndSearch<number>(
		`${organisation}/supplier/count`,
		filter.value,
		pagination.value,
		searchQuery,
	);

	const suppliersQuery = queryWithFilterPageAndSearch<SupplierDto[]>(
		`${organisation}/supplier/all`,
		filter.value,
		pagination.value,
		searchQuery,
	);

	return (
		<>
			<NewSupplierModal
				close={newModal.close}
				opened={newModal.isOpen}
				refreshTable={() => {
					suppliersQuery.remove();
					suppliersCountQuery.remove();

					suppliersQuery.refetch();
					suppliersCountQuery.refetch();
				}}
			/>
			<PageHeader title={'Suppliers'} hideFiltering>
				<Group>
					<SearchField
						placeholder={'Search suppliers...'}
						onDelayComplete={(value) => setSearchQuery(value)}
					/>
					<BranchSelector filter={filter} />
					<Button
						{...whiteIconButtonStyle}
						data-cy={'create-iconButton'}
						onClick={() => newModal.open()}
					>
						<IconPencilPlus {...defaultIconStyle} />
					</Button>
				</Group>
			</PageHeader>
			<DataTable
				pagination={pagination}
				totalItems={suppliersCountQuery.data?.value}
				isRowsLoading={suppliersQuery.isLoading}
				isPaginationLoading={suppliersCountQuery.isLoading}
				rows={pagination.value.pageSize}
			>
				<Table.Thead>
					<KeyedRow>
						<Table.Th>
							<Title label={'Name'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Reference'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Address'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Town'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Email'} />
						</Table.Th>
						<Table.Th>
							<Title label={'Phone'} />
						</Table.Th>
						<Table.Th w={'8vw'}>
							<Title label={'Website'} />
						</Table.Th>
						<Table.Th w={'8vw'}>
							<Title label={'Account No'} />
						</Table.Th>
						<Table.Th />
					</KeyedRow>
				</Table.Thead>
				<Table.Tbody>
					{suppliersQuery.data?.value.map((supplier) => (
						<SupplierRow
							initialSupplier={supplier}
							key={supplier.id}
							refreshTable={() => {
								suppliersQuery.remove();
								suppliersCountQuery.remove();

								suppliersQuery.refetch();
								suppliersCountQuery.refetch();
							}}
						/>
					))}
				</Table.Tbody>
			</DataTable>
		</>
	);
}
